import { Outlet, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Sidebar, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar/Sidebar';
import { Topbar } from './Topbar/Topbar';
import { BrandBanner } from './BrandBanner';
import { ALL_NAV_ITEMS } from './Sidebar/navItems';
import { useUiStore } from '../../store/ui.store';
import { useAlarmStream } from '../../hooks/useAlarmStream';
import { AnnouncementModal } from '../../features/announcements/AnnouncementModal';

function pageTitle(pathname: string): string {
  const match = ALL_NAV_ITEMS.find((item) => pathname.startsWith(item.path));
  return match?.label ?? 'AquaRefine';
}

/**
 * Desktop gets a persistent sidebar column (`hidden md:block`); mobile gets
 * the same <Sidebar> content inside a Radix Dialog styled as a slide-in
 * panel — no `useMediaQuery`/JS breakpoint check needed, Tailwind's
 * responsive classes handle which one is visible.
 *
 * BrandBanner sits above the sidebar+content row entirely, full page width
 * — the whole shell is one fixed-height column (banner + row) instead of
 * the page itself scrolling, so only <main> scrolls and the sidebar's own
 * height tracks whatever's left under the banner instead of a flat 100vh.
 *
 * `appChromeHidden` (toggled from within a page, e.g. the 3D view's expand
 * button) drops the banner/sidebar/topbar and <main>'s own padding entirely
 * so that page's content fills the full viewport edge to edge.
 */
export function AppLayout() {
  useAlarmStream();
  const location = useLocation();
  const mobileSidebarOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const chromeHidden = useUiStore((s) => s.appChromeHidden);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);

  return (
    <div className="flex h-screen flex-col bg-bg">
      <AnnouncementModal />
      {!chromeHidden && <BrandBanner />}

      <div className="flex min-h-0 flex-1">
        {!chromeHidden && (
          <aside
            className="hidden h-full shrink-0 border-r border-border bg-surface transition-[width] duration-150 md:block"
            style={{ width: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
          >
            <Sidebar />
          </aside>
        )}

        {!chromeHidden && (
          <Dialog.Root open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 md:hidden" />
              <Dialog.Content
                className="fixed inset-y-0 left-0 z-50 h-screen bg-surface shadow-xl outline-none md:hidden"
                style={{ width: SIDEBAR_WIDTH }}
                aria-describedby={undefined}
              >
                <Dialog.Title className="sr-only">Navigation</Dialog.Title>
                <Sidebar onNavigate={() => setMobileSidebarOpen(false)} />
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {!chromeHidden && <Topbar title={pageTitle(location.pathname)} onMenuClick={() => setMobileSidebarOpen(true)} />}
          <main className={`flex min-w-0 flex-1 flex-col ${chromeHidden ? 'overflow-hidden' : 'overflow-y-auto p-4 md:p-6'}`}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
